/**
 * `schemas/share.ts` — `POST /api/share/track`, `spec.md` §6.8.
 *
 * Two outcomes, and the client behaves differently for each:
 *  - `mode: "prepared"` — the bot had a `bot_file_id`, so the server called
 *    `savePreparedInlineMessage` and the client calls
 *    `Telegram.WebApp.shareMessage(preparedMessageId)`.
 *  - `mode: "link"` — a historical track the bot never saw, so there is no cached audio
 *    to attach; the client falls through to `openTelegramLink(shareUrl)`.
 *
 * The response is a discriminated union rather than one object with optional fields,
 * because a client that reads `preparedMessageId` without checking `mode` would call
 * `shareMessage(undefined)` and get a silent no-op.
 */
import * as z from 'zod';
import { NonEmptyText, TrackId, UnixSeconds } from './common.js';
/**
 * The chat kinds the prepared message may be sent to. Defaults per §6.8: user, group
 * and channel true, bot false.
 *
 * At least one MUST be true — Telegram otherwise produces an unsendable message, which
 * fails at share time rather than at request time and is therefore very hard to
 * diagnose. The rule is enforced on the request below, after defaults are applied.
 */
export const ShareAllowSchema = z.strictObject({
    user: z.boolean().default(true),
    group: z.boolean().default(true),
    channel: z.boolean().default(true),
    bot: z.boolean().default(false),
});
/**
 * `.prefault({})` rather than `.default({})`: in zod 4 a `.default()` value is returned
 * untouched as the output, so `.default({})` would hand the route a literal `{}` with
 * none of the four inner defaults applied — and the "at least one true" refinement
 * would then reject every request that omitted `allow`.
 */
export const ShareTrackRequestSchema = z
    .strictObject({
    trackId: TrackId,
    allow: ShareAllowSchema.prefault({}),
})
    .refine((body) => body.allow.user || body.allow.group || body.allow.channel || body.allow.bot, {
    error: 'at least one allow target must be true or Telegram produces an unsendable message',
    path: ['allow'],
});
export const SHARE_MODES = ['prepared', 'link'];
export const ShareMode = z.enum(SHARE_MODES);
/** A `startapp` deep link back into the Mini App at this track. */
export const DeepLinkUrl = z.url();
export const SharePreparedResponseSchema = z.strictObject({
    mode: z.literal('prepared'),
    /** The `savePreparedInlineMessage` id, e.g. `AgADBQ…`. Opaque, expires. */
    preparedMessageId: NonEmptyText,
    expiresAt: UnixSeconds,
    deepLink: DeepLinkUrl,
    shareUrl: DeepLinkUrl,
});
export const ShareLinkResponseSchema = z.strictObject({
    mode: z.literal('link'),
    deepLink: DeepLinkUrl,
    shareUrl: DeepLinkUrl,
});
export const ShareTrackResponseSchema = z.discriminatedUnion('mode', [
    SharePreparedResponseSchema,
    ShareLinkResponseSchema,
]);
/**
 * §6.8: "`result.id` is `trackId` truncated to **64 bytes** (it always fits, but
 * truncate defensively)." Exported so the bot code and its test share one number.
 */
export const INLINE_RESULT_ID_MAX_BYTES = 64;
//# sourceMappingURL=share.js.map